import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { privateApi } from "../../api/axiosConfig";
import InvoicesPageUI from "./InvoicesPageUI";

const PAYMENT_MESSAGES = {
  success: (trackId) => ({
    severity: "success",
    message: `پرداخت شما با موفقیت انجام شد. کد پیگیری: ${trackId || "ثبت شده"}`,
  }),
  failed: (trackId) => ({
    severity: "error",
    message: `پرداخت ناموفق بود یا توسط شما لغو شد. (کد پیگیری: ${trackId || "نامشخص"})`,
  }),
  error: () => ({
    severity: "error",
    message: "خطایی در تایید پرداخت رخ داد. لطفا با پشتیبانی تماس بگیرید.",
  }),
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  const currentPage = Math.max(
    1,
    Number.parseInt(searchParams.get("page") || "1", 10) || 1,
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const loadInvoices = useCallback(async (pageNumber) => {
    try {
      setLoading(true);
      setError(null);

      const response = await privateApi.get(
        `/api/v1/checkout/invoice/?page=${pageNumber}`,
      );

      const payload = response.data;
      const results = Array.isArray(payload?.results)
        ? payload.results
        : Array.isArray(payload)
          ? payload
          : [];

      setInvoices(results);
      setTotalCount(Number(payload?.count ?? results.length));
    } catch (err) {
      setError("مشکلی در دریافت تاریخچه سفارشات به وجود آمد.");
      setInvoices([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "تاریخچه سفارشات";

    const trackId = searchParams.get("trackId");

    // Strip one-time gateway params immediately (as you already do).
    if (searchParams.get("payment") || trackId) {
      const updatedParams = new URLSearchParams(searchParams);
      ["payment", "trackId", "message"].forEach((p) => updatedParams.delete(p));
      setSearchParams(updatedParams, { replace: true });
    }

    if (!trackId) {
      loadInvoices(currentPage); // normal visit, nothing to reconcile
      return;
    }

    // Authenticated reconcile: proves the result FOR the logged-in user.
    privateApi
      .get(`/api/v1/payments/status/?trackId=${trackId}`)
      .then(({ data }) => {
        const ok = data.status === "success" && data.order_status === "paid";
        setNotification(
          ok
            ? PAYMENT_MESSAGES.success(trackId)
            : PAYMENT_MESSAGES.failed(trackId),
        );
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401) {
          // authRedirect.js/ProtectedRoute only ever read `state.from`; a
          // `?next=` query string was built but silently dropped on return, so
          // the trackId (and therefore the payment result) was lost.
          navigate(`/login`, {
            replace: true,
            state: { from: `/invoices?trackId=${trackId}` },
          });
          return;
        }
        // 404 = someone else's (or unknown) trackId; anything else = gateway/backend error
        setNotification(PAYMENT_MESSAGES.error());
      })
      .finally(() => loadInvoices(currentPage)); 
  }, [loadInvoices, currentPage, searchParams, setSearchParams, navigate]);

  const handlePageChange = (newPage) => {
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", String(newPage));
    setSearchParams(updatedParams);
  };

  // The backend is the source of truth for invoice status.
  // Keep this callback only if the UI uses it for local countdown display.
  const handleExpire = useCallback((invoiceId) => {
    setInvoices((previous) =>
      previous.map((invoice) =>
        invoice.id === invoiceId && invoice.status?.toLowerCase() === "pending"
          ? { ...invoice, remaining_seconds: 0 }
          : invoice,
      ),
    );
  }, []);

  return (
    <InvoicesPageUI
      invoices={invoices}
      totalCount={totalCount}
      currentPage={currentPage}
      onPageChange={handlePageChange}
      loading={loading}
      error={error}
      notification={notification}
      onCloseNotification={() => setNotification(null)}
      onExpire={handleExpire}
      onNavigate={navigate}
    />
  );
}
